import {
  ApprovalUserRef,
  ReportApprovalWorkflow,
  ReportESignatureRecord,
  SignoffRole,
} from 'types'

import { getSignatureForRole, SIGNOFF_ROLE_LABELS } from './approvalRules'

export type NotificationChannel = 'inapp' | 'email'

export interface ApprovalNotificationRequest {
  user_id: string
  type:
    | 'report.approval.assigned'
    | 'report.approval.signed'
    | 'report.approval.voided'
  organization_id: string
  from_user_id: string
  channels: NotificationChannel[]
  payload: {
    title: string
    body: string
    cta: { label: string; url: string }
  }
}

export interface ApprovalNotificationContext {
  reportTitle: string
  organizationId: string
  reviewUrl: string
  actor: ApprovalUserRef
}

const CTA_REVIEW = 'Review and sign'
const CTA_OPEN = 'Open report'

export function buildAssignmentNotifications(
  previous: ReportApprovalWorkflow | undefined,
  next: ReportApprovalWorkflow,
  context: ApprovalNotificationContext
): ApprovalNotificationRequest[] {
  return next.requiredRoles.flatMap((role) => {
    const assignee = next.assignments[role]
    if (!assignee || assignee.userId === context.actor.userId) return []
    if (previous?.assignments[role]?.userId === assignee.userId) return []
    return [
      {
        user_id: assignee.userId,
        type: 'report.approval.assigned',
        organization_id: context.organizationId,
        from_user_id: context.actor.userId,
        channels: ['inapp', 'email'],
        payload: {
          title: `Your signature is needed on "${context.reportTitle}"`,
          body: `${context.actor.name} assigned you as ${SIGNOFF_ROLE_LABELS[role]}. Open the Review section to sign.`,
          cta: { label: CTA_REVIEW, url: context.reviewUrl },
        },
      },
    ]
  })
}

/** Only the final signature emails, so a three-role report is not three emails. */
export function buildSignedNotification(
  workflow: ReportApprovalWorkflow,
  records: readonly ReportESignatureRecord[],
  signedRole: SignoffRole,
  context: ApprovalNotificationContext
): ApprovalNotificationRequest | undefined {
  const recipient = workflow.configuredBy
  if (recipient.userId === context.actor.userId) return undefined

  const remaining = workflow.requiredRoles.filter(
    (role) => !getSignatureForRole(records, role)
  )
  const isComplete = remaining.length === 0
  const roleLabel = SIGNOFF_ROLE_LABELS[signedRole]

  return {
    user_id: recipient.userId,
    type: 'report.approval.signed',
    organization_id: context.organizationId,
    from_user_id: context.actor.userId,
    channels: isComplete ? ['inapp', 'email'] : ['inapp'],
    payload: {
      title: isComplete
        ? `"${context.reportTitle}" is fully signed`
        : `${context.actor.name} signed "${context.reportTitle}"`,
      body: isComplete
        ? `${context.actor.name} signed as ${roleLabel}. Every required signature is in, so the report can be published.`
        : `${context.actor.name} signed as ${roleLabel}. Still waiting on: ${remaining
            .map((role) => SIGNOFF_ROLE_LABELS[role])
            .join(', ')}.`,
      cta: { label: CTA_OPEN, url: context.reviewUrl },
    },
  }
}

export function buildVoidedNotifications(
  workflow: ReportApprovalWorkflow,
  records: readonly ReportESignatureRecord[],
  context: ApprovalNotificationContext
): ApprovalNotificationRequest[] {
  const recipients = new Map<string, ApprovalUserRef>()
  for (const record of records) {
    recipients.set(record.signerId, {
      userId: record.signerId,
      name: record.signerName,
      email: record.signerEmail,
    })
  }
  recipients.set(workflow.configuredBy.userId, workflow.configuredBy)
  recipients.delete(context.actor.userId)

  return [...recipients.values()].map((recipient) => ({
    user_id: recipient.userId,
    type: 'report.approval.voided',
    organization_id: context.organizationId,
    from_user_id: context.actor.userId,
    channels: ['inapp', 'email'],
    payload: {
      title: `Signatures voided on "${context.reportTitle}"`,
      body: `${context.actor.name} voided all signatures. Once a new draft is ready, each role must sign again.`,
      cta: { label: CTA_REVIEW, url: context.reviewUrl },
    },
  }))
}
